import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, nextTick } from "vue"
import TextEditor from "@/components/widgets/input/TextEditor.vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import { useQuery } from "@vue/apollo-composable"
import {
  FindConversationQuery,
  FindConversationQueryQuery,
  FindConversationQueryQueryVariables,
  UploadFileInput,
  Comment,
} from "@/gql"
import ChatComment from "@/modules/Missions/components/Details/Chat/ChatComment.vue"
import {
  AddCommentMutation,
  AddCommentMutationMutation,
  AddCommentMutationMutationVariables,
  UserAdvanced,
} from "@/gql"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import FileAttachmentPreview from "./FileAttachmentPreview.vue"
import UserAvatarGroup from "@/components/widgets/common/UserAvatarGroup.vue"
import { useAuth } from "@/store/auth/useAuth"

type Form = {
  text: string
  fileInput: UploadFileInput | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ChatDialog',
  props: {
    conversationId: null,
    collaborators: null,
    missionName: null
  },
  emits: ["close", "messageSent"],
  setup(__props: any, { emit }) {

const props = __props as {
  conversationId: string
  collaborators: UserAdvanced[]
  missionName: string
}




const { user } = useAuth()

const { addError } = useNotification()
const { mutate } = useMutation()

const isSubmitting = ref(false)

const isUserCollaborator = computed(() => {
  return props.collaborators.some((collaborator) => collaborator.id === user?.prn)
})

const { loading, onResult } = useQuery<
  FindConversationQueryQuery,
  FindConversationQueryQueryVariables
>(
  FindConversationQuery,
  {
    id: props.conversationId,
  },
  {
    pollInterval: 60000,
    keepPreviousResult: true,
  }
)

const comments = ref<Comment[] | null>(null)

const scrollableContent = ref<HTMLElement | null>(null)

const scrollToBottom = () => {
  nextTick(() => {
    if (scrollableContent.value) {
      scrollableContent.value.scrollTop = scrollableContent.value.scrollHeight
    }
  })
}

onResult((results) => {
  if (results.data?.findConversation?.comments) {
    comments.value = results.data?.findConversation?.comments as Comment[]
  }
  scrollToBottom()
})

const separatedComments = computed(() => {
  if (!comments.value) {
    return { todayComments: [] as Comment[], oldComments: [] as Comment[] }
  }

  const today = new Date().toISOString().slice(0, 10)

  const { todayComments, oldComments } = comments.value.reduce(
    (result: { todayComments: Comment[]; oldComments: Comment[] }, comment) => {
      const updatedAt = comment.updatedAt.slice(0, 10)
      if (updatedAt === today) {
        result.todayComments.push(comment)
      } else {
        result.oldComments.push(comment)
      }
      return result
    },
    { todayComments: [], oldComments: [] }
  )

  return { todayComments, oldComments }
})

const form = ref<Form>({
  text: "",
  fileInput: null,
})

const deleteFile = () => {
  form.value.fileInput = null
}

const editor = ref<InstanceType<typeof TextEditor> | null>(null)

const resetForm = () => {
  form.value.text = ""
  form.value.fileInput = null
  editor.value && editor.value.clearContent()
}

const isFileUploading = ref(false)

const onFileUploadComplete = () => {
  isFileUploading.value = false
}

const strippedFormText = computed(() => form.value.text.replace(/<[^>]+>/g, "").trim())
const isFormEmptyOrSubmitting = computed(
  () =>
    (!strippedFormText.value && !form.value.fileInput) ||
    isSubmitting.value ||
    isFileUploading.value
)

const onSendMessage = async () => {
  const strippedText = form.value.text.replace(/<[^>]+>/g, "").trim()

  if (!strippedText && !form.value.fileInput) {
    return addError("Please enter a message or attach a file")
  }

  isSubmitting.value = true

  const result = await mutate<AddCommentMutationMutation, AddCommentMutationMutationVariables>({
    mutation: AddCommentMutation,
    variables: {
      text: form.value.text,
      conversationId: props.conversationId,
      fileInput: form.value.fileInput,
    },
    done: (res) => {
      isSubmitting.value = false
      comments.value?.push(res?.data?.addComment?.comment as Comment)
      emit("messageSent", res?.data?.addComment?.comment as Comment)
      scrollToBottom()
    },
  })

  if (result?.data?.addComment && !result?.data?.addComment.error) {
    resetForm()
    return
  }

  isSubmitting.value = false
  comments.value?.pop()
}

const onClose = () => {
  resetForm()
  emit("close")
}

return { __sfc: true,props, emit, user, addError, mutate, isSubmitting, isUserCollaborator, loading, onResult, comments, scrollableContent, scrollToBottom, separatedComments, form, deleteFile, editor, resetForm, isFileUploading, onFileUploadComplete, strippedFormText, isFormEmptyOrSubmitting, onSendMessage, onClose, TextEditor, Icon, ChatComment, FileAttachmentPreview, UserAvatarGroup }
}

})